import type {
  ArtifactEvent,
  PhaseInsight,
  PredictionPayload,
  PredictionRequest,
  PredictionStreamEvent,
  ProductArtifactModule,
  SoulVoteEvent,
  WorkflowStepEvent,
} from "@/features/prediction/model/types";
import { PRODUCT_ARTIFACT_MODULES } from "@/features/prediction/model/types";
import type { PredictionFormValues } from "@/features/prediction/model/form";

export type PredictionRunStatus =
  | "idle"
  | "submitting"
  | "running"
  | "completed"
  | "failed"
  | "cancelled";

export interface PhaseState {
  status: "pending" | "running" | "done";
  ms?: number;
  total?: number;
  done?: number;
  insight?: PhaseInsight;
}

export interface PredictionRunState {
  status: PredictionRunStatus;
  request: PredictionRequest | null;
  review: PredictionFormValues | null;
  payload: PredictionPayload | null;
  phases: Record<string, PhaseState>;
  liveQuotes: SoulVoteEvent[];
  artifacts: Partial<Record<ProductArtifactModule, ArtifactEvent>>;
  workflowSteps: Partial<
    Record<WorkflowStepEvent["step"], WorkflowStepEvent["status"]>
  >;
  startedAt: number | null;
  finishedAt: number | null;
  error: string | null;
}

export const initialPredictionRunState: PredictionRunState = {
  status: "idle",
  request: null,
  review: null,
  payload: null,
  phases: {},
  liveQuotes: [],
  artifacts: {},
  workflowSteps: {},
  startedAt: null,
  finishedAt: null,
  error: null,
};

export type PredictionRunAction =
  | {
      type: "submit";
      request: PredictionRequest;
      review: PredictionFormValues;
      startedAt: number;
    }
  | { type: "event"; event: PredictionStreamEvent }
  | { type: "complete"; payload: PredictionPayload; finishedAt: number }
  | { type: "fail"; message: string; finishedAt: number }
  | { type: "cancel"; finishedAt: number }
  | {
      type: "show";
      request: PredictionRequest;
      review: PredictionFormValues | null;
      payload: PredictionPayload;
    }
  | {
      type: "restore";
      status: "running" | "failed";
      request: PredictionRequest;
      review: PredictionFormValues | null;
      startedAt: number;
      currentStep: string | null;
      error: string | null;
    }
  | { type: "reset" };

export function predictionRunReducer(
  state: PredictionRunState,
  action: PredictionRunAction,
): PredictionRunState {
  switch (action.type) {
    case "submit":
      return {
        ...initialPredictionRunState,
        status: "submitting",
        request: action.request,
        review: action.review,
        startedAt: action.startedAt,
      };
    case "event": {
      const event = action.event;
      if (event.type === "phase") {
        const previous = state.phases[event.name] || { status: "pending" };
        const phase: PhaseState =
          event.status === "start"
            ? {
                ...previous,
                status: "running",
                total: event.total ?? previous.total,
                done: 0,
              }
            : {
                ...previous,
                status: "done",
                ms: event.ms,
                insight: event.insight,
              };
        const phases = { ...state.phases, [event.name]: phase };
        if (event.name === "schema_outputs" && event.status === "done") {
          phases.report = { status: "running" };
        }
        return {
          ...state,
          status: "running",
          phases,
        };
      }
      if (event.type === "soul_vote") {
        const previous = state.phases.souls || { status: "running" };
        return {
          ...state,
          status: "running",
          liveQuotes: [event, ...state.liveQuotes].slice(0, 20),
          phases: {
            ...state.phases,
            souls: {
              ...previous,
              status: "running",
              done: event.done,
              total: event.total ?? previous.total,
            },
          },
        };
      }
      if (event.type === "workflow_step") {
        return {
          ...state,
          status: event.status === "failed" ? "failed" : "running",
          workflowSteps: {
            ...state.workflowSteps,
            [event.step]: event.status,
          },
        };
      }
      if (event.type === "artifact") {
        const previous = state.artifacts[event.module];
        if (previous && previous.revision >= event.revision) return state;
        return {
          ...state,
          status: "running",
          artifacts: {
            ...state.artifacts,
            [event.module]: event,
          },
        };
      }
      if (event.type === "final") {
        return {
          ...state,
          status: "completed",
          payload: event.payload,
          artifacts: mergePayloadArtifacts(state.artifacts, event.payload),
          finishedAt: Date.now(),
          phases: {
            ...state.phases,
            report: {
              ...(state.phases.report || {}),
              status: "done",
            },
          },
        };
      }
      if (event.type === "error") {
        return {
          ...state,
          status: "failed",
          error: event.message || "Prediction stream error",
          finishedAt: Date.now(),
        };
      }
      return state;
    }
    case "complete":
      return {
        ...state,
        status: "completed",
        payload: action.payload,
        artifacts: mergePayloadArtifacts(state.artifacts, action.payload),
        finishedAt: action.finishedAt,
      };
    case "fail":
      return {
        ...state,
        status: "failed",
        error: action.message,
        finishedAt: action.finishedAt,
      };
    case "cancel":
      return {
        ...state,
        status: "cancelled",
        finishedAt: action.finishedAt,
      };
    case "show":
      return {
        ...initialPredictionRunState,
        status: "completed",
        request: action.request,
        review: action.review,
        payload: action.payload,
        artifacts: mergePayloadArtifacts({}, action.payload),
      };
    case "restore":
      return {
        ...initialPredictionRunState,
        status: action.status,
        request: action.request,
        review: action.review,
        startedAt: action.startedAt,
        error: action.error,
        phases: restoredPhases(action.currentStep),
      };
    case "reset":
      return initialPredictionRunState;
  }
}

function restoredPhases(currentStep: string | null): Record<string, PhaseState> {
  if (currentStep === "report_generation") {
    return {
      scenario: { status: "done" },
      world_model: { status: "done" },
      agents: { status: "done" },
      souls: { status: "done" },
      mc_runner: { status: "done" },
      hawkes: { status: "done" },
      extras: { status: "done" },
      schema_outputs: { status: "running" },
    };
  }
  if (currentStep === "simulation_process") {
    return {
      scenario: { status: "done" },
      world_model: { status: "done" },
      agents: { status: "done" },
      souls: { status: "running" },
    };
  }
  return { scenario: { status: "running" } };
}

function mergePayloadArtifacts(
  current: PredictionRunState["artifacts"],
  payload: PredictionPayload,
): PredictionRunState["artifacts"] {
  const modules = payload.product_modules;
  if (!modules) return current;
  const merged = { ...current };
  for (const module of PRODUCT_ARTIFACT_MODULES) {
    const modulePayload = modules[module];
    if (!modulePayload || merged[module]) continue;
    merged[module] = {
      type: "artifact",
      module,
      revision: 1,
      payload: modulePayload,
    };
  }
  return merged;
}
